import React, { useEffect, useState } from "react";
import { Save, Copy, Loader2 } from "lucide-react";
import { Panel, MonoLabel } from "@/components/Panel";
import { toast } from "sonner";
import { api, errText, fieldErrors } from "@/lib/devadminApi";

const STATUSES = ["draft", "published", "live", "completed", "archived"];
const FIELDS = [["name", "Event name"], ["slug", "Public URL slug"], ["date", "Date (YYYY-MM-DD)"], ["early_bird_until", "Early bird until (YYYY-MM-DD)"], ["region", "Region"], ["city", "City"], ["venue", "Venue"]];
const EDITABLE = ["name", "slug", "date", "early_bird_until", "region", "city", "venue", "status"];

export function EventDetails({ event, onSaved, onCloned }) {
  const [form, setForm] = useState(event);
  const [cfg, setCfg] = useState({});
  const [errs, setErrs] = useState({});
  const [busy, setBusy] = useState("");
  const [cloneName, setCloneName] = useState("");
  useEffect(() => {
    setForm(event);
    setCfg(event.event_config || {});
    setErrs({});
  }, [event]);

  const save = async () => {
    const diff = Object.fromEntries(Object.entries(form).filter(([k, v]) => EDITABLE.includes(k) && v !== event[k]));
    const cfgDiff = {};
    const beforeCfg = event.event_config || {};
    if ((cfg.register_contact_message || "") !== (beforeCfg.register_contact_message || "")) {
      cfgDiff.register_contact_message = cfg.register_contact_message || "";
    }
    if (!Object.keys(diff).length && !Object.keys(cfgDiff).length) return toast.info("No changes");
    setBusy("save"); setErrs({});
    try {
      let data = event;
      if (Object.keys(diff).length) {
        const r = await api.patch(`/events/${event.id}`, diff);
        data = r.data;
      }
      if (Object.keys(cfgDiff).length) {
        await api.patch(`/events/${event.id}/config`, cfgDiff);
        data = { ...data, event_config: { ...beforeCfg, ...cfgDiff } };
      }
      onSaved(data); toast.success("Event updated · audit logged");
    } catch (e) { setErrs(fieldErrors(e)); toast.error(errText(e)); } finally { setBusy(""); }
  };

  const clone = async () => {
    if (cloneName.trim().length < 3) return setErrs({ clone: "Give the new event a name (3+ chars)" });
    setBusy("clone");
    try {
      const { data } = await api.post(`/events/${event.id}/clone`, { name: cloneName.trim() });
      toast.success(`Cloned → ${data.name} · ${data.cloned.fee_tiers} tiers, ${data.cloned.meal_windows} meal windows`);
      setCloneName(""); onCloned(data);
    } catch (e) { setErrs(fieldErrors(e)); toast.error(errText(e)); } finally { setBusy(""); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Panel className="p-6 lg:col-span-2" data-testid="event-details-panel">
        <MonoLabel>Event Details</MonoLabel>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FIELDS.map(([k, label]) => (
            <div key={k} className={k === "name" || k === "venue" ? "sm:col-span-2" : ""}>
              <MonoLabel>{label}</MonoLabel>
              <input data-testid={`event-${k}`} className="input-x mt-2" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              {errs[k] && <div data-testid={`event-err-${k}`} className="text-xs mt-1" style={{ color: "var(--red-2)" }}>{errs[k]}</div>}
            </div>
          ))}
          <div className="sm:col-span-2">
            <MonoLabel>Status</MonoLabel>
            <div className="mt-2 flex flex-wrap gap-0">
              {STATUSES.map((s, i) => (
                <button key={s} data-testid={`event-status-${s}`} onClick={() => setForm({ ...form, status: s })}
                  className={`px-4 py-2 hairline text-xs mono-label ${form.status === s ? "!border-[var(--red)] text-[var(--text)]" : "text-[var(--taupe)]"}`}
                  style={{ marginLeft: i ? -1 : 0, background: form.status === s ? "rgba(204,37,47,0.12)" : "transparent" }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2">
            <MonoLabel>Stall &amp; Sponsor contact message (public landing)</MonoLabel>
            <textarea
              data-testid="event-register-contact-message"
              className="input-x mt-2"
              rows={3}
              placeholder="For Stall and Sponsor registrations, please contact +91 94495 81062"
              value={cfg.register_contact_message || ""}
              onChange={(e) => setCfg({ ...cfg, register_contact_message: e.target.value })}
            />
            <div className="mono-label mt-2" style={{ color: "var(--taupe)" }}>
              Shown on the public page under the "Stall &amp; Sponsor" tab — include a phone number so visitors can call or WhatsApp directly.
            </div>
          </div>
        </div>
        <button data-testid="event-save-btn" className="btn-red mt-6" disabled={!!busy} onClick={save}>
          {busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save event
        </button>
      </Panel>

      <Panel className="p-6" data-testid="event-clone-panel">
        <MonoLabel>Clone for next event</MonoLabel>
        <p className="text-sm mt-3" style={{ color: "var(--taupe)" }}>
          Copies fee tiers, meal windows, categories & rounds into a fresh <span className="font-mono">draft</span> event. Integration keys are not copied.
        </p>
        <input data-testid="event-clone-name" className="input-x mt-4" placeholder="e.g. BNI Davangere Members Day 2027" value={cloneName} onChange={(e) => setCloneName(e.target.value)} />
        {errs.clone && <div className="text-xs mt-1" style={{ color: "var(--red-2)" }}>{errs.clone}</div>}
        <button data-testid="event-clone-btn" className="btn-ghost mt-4 w-full justify-center" disabled={!!busy} onClick={clone}>
          {busy === "clone" ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />} Clone event
        </button>
        <div className="mono-label mt-5" style={{ color: "var(--taupe)" }}>ID · {event.id}</div>
        {event.cloned_from && <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>Cloned from · {event.cloned_from.slice(0, 8)}…</div>}
      </Panel>
    </div>
  );
}
